const selectors = {
  productGridItemLink: '[data-grid-link]',
  dataGridImages: 'data-grid-images',
  dataGridImage: 'data-grid-image',
  dataGridImageTarget: 'data-grid-image-target',
  dataGridCurrentImage: 'data-grid-current-image',
  dataGridPagination: 'data-grid-pagination',
  dataGridPage: 'data-grid-page',
  dataGridItemVariant: 'data-grid-item-variant',
  dataSlideForFilterSelectedVariant: 'data-slide-for-filter-selected-variant',
  dataSlideForVariantMedia: 'data-slide-for-variant-media',
  dataSlideshowStyle: 'data-slideshow-style',
  styleWidth: '--width',
};

const classes = {
  fade: 'is-fade',
  active: 'is-active',
  mobile: 'is-mobile',
  pagination: 'product-grid-item__pagination',
  productGridItemImage: 'product-grid-item__image',
};

// Only create pagination if the style is cycle_images
// If the style is second_immediately we show the second image immmidiately
// without pagination or a timer. If the style is disabled we do nothing on
// hover, only change images on swatch click

class ProductGridItemVariant extends HTMLElement {
  static get observedAttributes() {
    return ['hidden'];
  }

  constructor() {
    super();

    this.container = this;
    this.mobileStopSlideshow = 'true';
    this.timeout = window.theme.settings.cycle_images_hover_delay * 1000 || 1500;
    this.interval = null;
    this.pagingProgressPause = false;
    this.pagingProgressCounter = 0;
    this.siblingsFetchCounter = 0;
    this.siblingLoadImageIndex = null;
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // As soon as grid item is on-screen preload second slideshow image so that it appears immediately when hovering
            if (this.imageCount > 1) {
              this.preloadImage(1);
            }
          }
        });
      },
      {threshold: 0.1}
    );
  }

  /*
  Ensure that image at index is loaded
  */
  preloadImage(index) {
    // For now, we don't have a mobile slideshow so do not preload
    if (this.isMobile) return;

    const childElement = [...this.images][index];

    if (!childElement) {
      throw new Error(`No child element at index ${index}`);
    }

    childElement.setAttribute('loading', 'eager');
  }

  /*
  Load all images by replacing each template with it's content
  */
  preloadImages() {
    [...this.imagesHolder.children].forEach((_el, i) => this.preloadImage(i));
  }

  connectedCallback() {
    this.toggleMobile();
    document.addEventListener('theme:resize:width', () => this.toggleMobile());

    if (this.isMobile) return;

    if (this.imageCount > 1) {
      // Create slider of PGI images if the images are more than 1
      this.createPaging();

      this.container.addEventListener('theme:swatch:change', this.swatchChangeEvent);
      this.container.addEventListener('mouseenter', this.mouseEnterEvent);
      this.container.addEventListener('mouseleave', this.mouseLeaveEvent);
    }

    this.intersectionObserver.observe(this.container);
  }

  disconnectedCallback() {
    this.container.removeEventListener('theme:swatch:change', this.swatchChangeEvent);
    this.container.removeEventListener('mouseenter', this.mouseEnterEvent);
    this.container.removeEventListener('mouseleave', this.mouseLeaveEvent);
  }

  swatchChangeEvent(e) {
    // Change the image if the swatch is clicked
    const id = e.detail.id;
    const target = this.container.querySelector(`[${selectors.dataGridImageTarget}="${id}"]`);

    if (target) {
      const index = target.getAttribute(selectors.dataGridImage);

      this.pagingProgressCounter = 0;
      this.changeImage(index);

      if (e.detail.stopSlideAnimation) {
        this.stopPaging();
      }
    }
  }

  mouseEnterEvent() {
    this.preloadImages();

    switch (this.slideshowStyle) {
      case 'cycle_images':
        // Start slideshow
        this.pagingProgressPause = this.isMobile;
        this.progressPaging();
        break;
      case 'second_immediately':
        this.changeImage(this.getNextIndex());
        break;
      case 'disabled':
        // Do nothing unless swatch is clicked
        break;
      default:
        throw new Error(`Unknown option ${this.slideshowStyle}`);
    }
  }

  mouseLeaveEvent() {
    // Stop slideshow and rewind to first image
    this.pagingProgressPause = true;
    this.resetSlideshow();
    this.progressPaging();
  }

  attributeChangedCallback(name, prevValue, newValue) {
    if (name === 'hidden' && prevValue == null && newValue != null) {
      // When hiding or showing a PGI, reset it's slideshow to show the image for the current variant
      this.showVariant();
    }
  }

  toggleMobile() {
    this.container.classList.toggle(classes.mobile, this.isMobile);
  }

  get isMobile() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    return windowWidth < window.theme.sizes.medium;
  }

  resetSlideshow() {
    const forcedDefaultSlide = this.container.querySelector(`[${selectors.dataSlideForFilterSelectedVariant}]`);
    // if a variant is selected by a collection filter show the variant image instead of the image at [0]
    if (forcedDefaultSlide) {
      let imageIndex = Number(forcedDefaultSlide.getAttribute(selectors.dataGridImage));
      this.changeImage(imageIndex);
    } else {
      this.changeImage(0);
    }
    this.pagingProgressPause = true;
  }

  showVariant() {
    const variantImage = this.container.querySelector(`[${selectors.dataSlideForVariantMedia}]`);
    // if a swatch is clicked, show the variant image instead of the image at [0]
    if (variantImage) {
      let imageIndex = Number(variantImage.getAttribute(selectors.dataGridImage));
      this.changeImage(imageIndex);
    } else {
      this.changeImage(0);
    }
    this.pagingProgressPause = true;
  }

  getNextIndex() {
    let currentImage = this.container.querySelector(`[${selectors.dataGridImage}][${selectors.dataGridCurrentImage}]`);
    if (currentImage) {
      const currentIndex = Number(currentImage.getAttribute(selectors.dataGridImage));
      const nextIndex = (currentIndex + 1) % this.imageCount;
      return nextIndex;
    } else {
      return 0;
    }
  }

  changeImage(index) {
    const currentImage = this.images[index % this.imageCount];

    if (!currentImage) return;

    this.activeImages.forEach((image) => image.removeAttribute(selectors.dataGridCurrentImage));

    currentImage.setAttribute(selectors.dataGridCurrentImage, 'true');

    if (this.slideshowStyle === 'cycle_images') {
      this.changePaging(index);
    }
  }

  changePaging(index = 0) {
    // Change active class on slider pagination
    const activePage = this.container.querySelector(`[${selectors.dataGridPage}].${classes.active}`);
    const currentPage = this.container.querySelector(`[${selectors.dataGridPage}="${index}"]`);

    if (activePage) {
      activePage.style.setProperty(selectors.styleWidth, '100%');
      activePage.classList.remove(classes.active);
    }

    if (currentPage) {
      currentPage.classList.add(classes.active);
      this.progressPaging();
    }
  }

  progressPaging() {
    // Play/pause the PGI images slider
    const element = this.container.querySelector(`[${selectors.dataGridPage}].${classes.active}`);
    if (!element || this.isMobile) return;

    this.stopPaging();

    if (this.pagingProgressCounter === 0) {
      element.style.setProperty(selectors.styleWidth, '0%');
    }

    this.interval = setInterval(() => {
      if (this.pagingProgressCounter >= 100 && !this.pagingProgressPause) {
        this.stopPaging();
        this.pagingProgressCounter = 0;
        this.changeImage(this.getNextIndex());
      } else if (!this.pagingProgressPause) {
        this.pagingProgressCounter++;
        element.style.setProperty(selectors.styleWidth, `${this.pagingProgressCounter}%`);
      }
    }, this.timeout / 100);
  }

  stopPaging() {
    // Stop slider
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  createPaging() {
    // Create pagination of PGI images slider
    if (this.imagesHolder && this.slideshowStyle === 'cycle_images') {
      let pagination = '';

      for (let index = 0; index < this.imageCount; index++) {
        let activeClass = '';
        if (index === 0) {
          activeClass = `class="${classes.active}"`;
        }
        pagination += `<span ${activeClass} ${selectors.dataGridPage}="${index}">${index + 1}</span>`;
      }

      if (pagination !== '') {
        let pagingContainer = this.container.querySelector(`[${selectors.dataGridPagination}]`);
        if (!pagingContainer) {
          pagingContainer = document.createElement('div');
          pagingContainer.className = classes.pagination;
          pagingContainer.setAttribute(selectors.dataGridPagination, '');
        }

        pagingContainer.innerHTML = pagination;
        this.imagesHolder.parentElement.prepend(pagingContainer);
      }
    }
  }

  removePaging() {
    // Remove the slider pagination holder
    const pagingContainer = this.container.querySelector(`[${selectors.dataGridPagination}]`);
    if (pagingContainer) {
      pagingContainer.remove();
    }
  }

  get imagesHolder() {
    return this.container.querySelector(`[${selectors.dataGridImages}]`);
  }

  get images() {
    return this.imagesHolder.querySelectorAll(`[${selectors.dataGridImage}]`);
  }

  get activeImages() {
    return this.imagesHolder.querySelectorAll(`[${selectors.dataGridImage}][${selectors.dataGridCurrentImage}]`);
  }

  get links() {
    return this.container.querySelectorAll(selectors.productGridItemLink);
  }

  get slideshowStyle() {
    return this.container.getAttribute(selectors.dataSlideshowStyle);
  }

  get imageCount() {
    return this.images.length;
  }
}

export {ProductGridItemVariant};
